// ────────────────────────────────────────────────────────────────────────────
// One-time lift of locally-saved layouts into Directory.
//
// Layouts used to live in `localStorage` while the backend was being built, so
// anyone who designed one before the swap has it in their browser and nowhere
// else. Losing that to a storage change would be a poor showing for a feature
// whose whole point is not losing work.
//
// Deliberately conservative:
//   * runs only when the organisation has NO saved layouts on the server, so it
//     can never duplicate or overwrite what a colleague has already put there;
//   * only custom layouts move — the stock ones are code and already present;
//   * DUPLICATES ARE COLLAPSED FIRST. The local store was a scratchpad: hitting
//     Duplicate five times left five identical "Invoice layout (copy)" entries
//     in it, and lifting them verbatim wrote five identical rows into the
//     organisation — shared with everybody, and a chore to clear out. What was
//     private clutter must not become everyone's;
//   * a marker is written first, so a failure mid-way cannot re-import on the
//     next render and pile up copies;
//   * the local data is left in place. If the import went wrong, the source is
//     still there to look at.
// ────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react';
import { LAYOUT_SCHEMA_VERSION, type DocumentLayout, type DocumentType } from './types';

/** Where the old zustand store persisted to. */
const LEGACY_KEY = 'my:tms:documentTemplates';
/** Written once the lift has been attempted, per organisation. */
const IMPORTED_KEY = 'my:tms:documentTemplates:imported';

interface LegacyTemplate {
  id: string;
  docType: DocumentType;
  name: string;
  isDefault: boolean;
  isStock: boolean;
  schemaVersion: number;
  layout: DocumentLayout;
}

/** Custom layouts sitting in localStorage, or an empty list. */
export function readLegacyTemplates(): LegacyTemplate[] {
  try {
    const raw = globalThis.localStorage?.getItem(LEGACY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { state?: { templates?: LegacyTemplate[] } };
    const all = parsed.state?.templates ?? [];
    return all.filter((t) => t && !t.isStock && t.layout);
  } catch {
    // Malformed or unreadable — nothing to lift, and nothing worth reporting.
    return [];
  }
}

/**
 * Collapse the local store's duplicates before anything is written.
 *
 * <p>
 * Two entries are the same layout when they agree on kind, name AND content —
 * that is a Duplicate nobody renamed, and only the first is carried across.
 * Entries that merely SHARE a name are different work, so they all travel and
 * the later ones are numbered, which keeps them apart in the list instead of
 * arriving as a row of identical names.
 * </p>
 */
export function dedupeLegacyTemplates(templates: LegacyTemplate[]): LegacyTemplate[] {
  const seen = new Set<string>();
  const usedNames = new Set<string>();
  const out: LegacyTemplate[] = [];

  for (const t of templates) {
    const identity = `${t.docType}\u0000${t.name}\u0000${JSON.stringify(t.layout)}`;
    if (seen.has(identity)) continue;
    seen.add(identity);

    let name = t.name;
    const nameKey = () => `${t.docType}\u0000${name}`;
    for (let n = 2; usedNames.has(nameKey()); n++) name = `${t.name} (${n})`;
    usedNames.add(nameKey());

    out.push(name === t.name ? t : { ...t, name });
  }

  return out;
}

function alreadyImported(organisationId: string): boolean {
  try {
    const raw = globalThis.localStorage?.getItem(IMPORTED_KEY);
    return raw ? (JSON.parse(raw) as string[]).includes(organisationId) : false;
  } catch {
    return false;
  }
}

function markImported(organisationId: string): void {
  try {
    const raw = globalThis.localStorage?.getItem(IMPORTED_KEY);
    const done = raw ? (JSON.parse(raw) as string[]) : [];
    if (!done.includes(organisationId)) {
      globalThis.localStorage?.setItem(IMPORTED_KEY, JSON.stringify([...done, organisationId]));
    }
  } catch {
    /* storage disabled — the import still ran, it just may retry once */
  }
}

export interface LocalTemplateImport {
  /** Names lifted in this session, for a one-off confirmation to the operator. */
  imported: string[];
}

/**
 * Lift local layouts the first time this organisation loads with an empty
 * server list.
 *
 * `serverIsEmpty` must reflect a *loaded* list — passing it while the query is
 * still pending would import on top of layouts that simply had not arrived yet.
 */
export function useLocalTemplateImport(
  organisationId: string | null,
  serverIsEmpty: boolean,
  createTemplate: (
    docType: DocumentType,
    name: string,
    layout: DocumentLayout,
    schemaVersion: number,
  ) => Promise<unknown>,
): LocalTemplateImport {
  const [imported, setImported] = useState<string[]>([]);
  // Guards against a second pass while the first is still in flight — the
  // effect's inputs change as each create resolves.
  const running = useRef(false);

  useEffect(() => {
    if (!organisationId || !serverIsEmpty || running.current) return;
    if (alreadyImported(organisationId)) return;

    const legacy = dedupeLegacyTemplates(readLegacyTemplates());
    if (legacy.length === 0) {
      markImported(organisationId);
      return;
    }

    running.current = true;
    void (async () => {
      const done: string[] = [];
      // Marked before the writes: a half-finished import that re-ran would
      // create duplicates, which is worse than one that needs a manual nudge.
      markImported(organisationId);
      for (const t of legacy) {
        try {
          await createTemplate(
            t.docType,
            t.name,
            t.layout,
            t.schemaVersion ?? LAYOUT_SCHEMA_VERSION,
          );
          done.push(t.name);
        } catch {
          // Skip the ones that fail; the rest still make it across.
        }
      }
      setImported(done);
    })();
  }, [organisationId, serverIsEmpty, createTemplate]);

  return { imported };
}
